import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  GripVertical,
  Eye,
  EyeOff,
  Type,
  Image,
  Save,
  X,
  Settings,
  Layout,
  Plus,
  Trash2,
  Copy,
  Move,
  Minus,
  Square,
  FileText,
  Grid3X3,
  ZoomIn,
  ZoomOut,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Upload,
  QrCode,
  Table,
  Hash,
  User,
  Building,
  CreditCard,
  MessageSquare,
  Palette,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// A4 dimensions in mm
const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const GRID_SIZE = 5; // 5mm grid
const SCALE_FACTOR = 2.5; // Scale for display (pixels per mm)

// Available fonts
const FONTS = [
  { id: 'Helvetica', name: 'Helvetica' },
  { id: 'Arial', name: 'Arial' },
  { id: 'Times-Roman', name: 'Times New Roman' },
  { id: 'Courier', name: 'Courier' },
  { id: 'Georgia', name: 'Georgia' },
];

// Font sizes
const FONT_SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 42, 48];

// Block types with default properties
const BLOCK_TYPES = {
  logo: {
    name: { fr: 'Logo', nl: 'Logo' },
    icon: Image,
    defaultWidth: 40,
    defaultHeight: 20,
    defaultContent: '',
  },
  company_header: {
    name: { fr: 'En-tête Entreprise', nl: 'Bedrijfskoptekst' },
    icon: Building,
    defaultWidth: 80,
    defaultHeight: 30,
    defaultContent: 'Votre Entreprise\n123 Rue Exemple\n1000 Bruxelles\nTVA: BE0123456789',
  },
  invoice_info: {
    name: { fr: 'N° Facture / Date', nl: 'Factuurnr. / Datum' },
    icon: Hash,
    defaultWidth: 60,
    defaultHeight: 25,
    defaultContent: 'Facture N°: FAC-2024-0001\nDate: 01/01/2024\nÉchéance: 31/01/2024',
  },
  client_info: {
    name: { fr: 'Infos Client', nl: 'Klantgegevens' },
    icon: User,
    defaultWidth: 70,
    defaultHeight: 35,
    defaultContent: 'Facturé à:\nNom du Client\n456 Avenue Client\n2000 Anvers',
  },
  items_table: {
    name: { fr: 'Tableau Articles', nl: 'Artikeltabel' },
    icon: Table,
    defaultWidth: 170,
    defaultHeight: 60,
    defaultContent: 'TABLE',
  },
  totals: {
    name: { fr: 'Totaux', nl: 'Totalen' },
    icon: CreditCard,
    defaultWidth: 60,
    defaultHeight: 30,
    defaultContent: 'Sous-total HTVA: €0.00\nTVA 21%: €0.00\nTOTAL TTC: €0.00',
  },
  qr_code: {
    name: { fr: 'QR Code Paiement', nl: 'Betaling QR-code' },
    icon: QrCode,
    defaultWidth: 30,
    defaultHeight: 35,
    defaultContent: 'QR',
  },
  payment_conditions: {
    name: { fr: 'Conditions de paiement', nl: 'Betalingsvoorwaarden' },
    icon: FileText,
    defaultWidth: 100,
    defaultHeight: 15,
    defaultContent: 'Paiement sous 30 jours. IBAN: BE00 0000 0000 0000',
  },
  footer: {
    name: { fr: 'Pied de page', nl: 'Voettekst' },
    icon: Layout,
    defaultWidth: 190,
    defaultHeight: 10,
    defaultContent: 'Merci pour votre confiance.',
  },
  free_text: {
    name: { fr: 'Texte libre', nl: 'Vrije tekst' },
    icon: Type,
    defaultWidth: 50,
    defaultHeight: 15,
    defaultContent: 'Votre texte ici',
  },
  free_image: {
    name: { fr: 'Image libre', nl: 'Vrije afbeelding' },
    icon: Image,
    defaultWidth: 40,
    defaultHeight: 30,
    defaultContent: '',
  },
  separator: {
    name: { fr: 'Ligne de séparation', nl: 'Scheidingslijn' },
    icon: Minus,
    defaultWidth: 170,
    defaultHeight: 1,
    defaultContent: '',
  },
};

// Draggable Block Component
const DraggableBlock = ({ 
  block, 
  isSelected, 
  onSelect, 
  onMove, 
  onResize,
  scale,
  snapToGrid,
  language 
}) => {
  const blockRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ width: 0, height: 0, x: 0, y: 0 });

  const snapValue = (value) => {
    if (snapToGrid) {
      return Math.round(value / GRID_SIZE) * GRID_SIZE;
    }
    return value;
  };

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - block.x * scale,
      y: e.clientY - block.y * scale,
    });
    onSelect(block.id);
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      width: block.width,
      height: block.height,
      x: e.clientX,
      y: e.clientY,
    });
    onSelect(block.id);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = snapValue((e.clientX - dragStart.x) / scale);
        const newY = snapValue((e.clientY - dragStart.y) / scale);
        // Clamp to page bounds
        const clampedX = Math.max(0, Math.min(A4_WIDTH - block.width, newX));
        const clampedY = Math.max(0, Math.min(A4_HEIGHT - block.height, newY));
        onMove(block.id, clampedX, clampedY);
      }
      if (isResizing) {
        const deltaX = (e.clientX - resizeStart.x) / scale;
        const deltaY = (e.clientY - resizeStart.y) / scale;
        const newWidth = snapValue(Math.max(10, resizeStart.width + deltaX));
        const newHeight = snapValue(Math.max(5, resizeStart.height + deltaY));
        // Clamp to page bounds
        const clampedWidth = Math.min(A4_WIDTH - block.x, newWidth);
        const clampedHeight = Math.min(A4_HEIGHT - block.y, newHeight);
        onResize(block.id, clampedWidth, clampedHeight);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, resizeStart, block, scale, onMove, onResize, snapToGrid]);

  const blockType = BLOCK_TYPES[block.type];
  const Icon = blockType?.icon || Square;

  const getBlockContent = () => {
    if (block.type === 'separator') {
      return (
        <div 
          className="w-full h-full"
          style={{ 
            backgroundColor: block.borderColor || '#000000',
          }}
        />
      );
    }
    
    if (block.type === 'logo' || block.type === 'free_image') {
      if (block.imageUrl) {
        return (
          <img 
            src={block.imageUrl} 
            alt="" 
            className="w-full h-full object-contain"
          />
        );
      }
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300">
          <Image className="w-6 h-6 text-gray-400" />
        </div>
      );
    }

    if (block.type === 'qr_code') {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-white border border-gray-300">
          <div className="w-3/4 h-3/4 border border-gray-400 flex items-center justify-center text-xs text-gray-500">
            QR
          </div>
          <span className="text-[8px] text-gray-500 mt-1">Scan to pay</span>
        </div>
      );
    }

    if (block.type === 'items_table') {
      return (
        <div className="w-full h-full overflow-hidden" style={{ fontSize: `${(block.fontSize || 10) * 0.8}px` }}>
          <table className="w-full border-collapse">
            <thead>
              <tr style={{ backgroundColor: block.headerColor || '#8B4513', color: '#fff' }}>
                <th className="p-1 text-left border">Description</th>
                <th className="p-1 text-right border w-12">Qté</th>
                <th className="p-1 text-right border w-16">Prix HT</th>
                <th className="p-1 text-right border w-16">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-1 border">Article exemple</td>
                <td className="p-1 text-right border">2</td>
                <td className="p-1 text-right border">€20.66</td>
                <td className="p-1 text-right border">€41.32</td>
              </tr>
              <tr style={{ backgroundColor: block.altRowColor || '#f9f9f9' }}>
                <td className="p-1 border">Autre article</td>
                <td className="p-1 text-right border">1</td>
                <td className="p-1 text-right border">€61.98</td>
                <td className="p-1 text-right border">€61.98</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div 
        className="w-full h-full p-1 overflow-hidden whitespace-pre-wrap"
        style={{
          fontFamily: block.fontFamily || 'Helvetica',
          fontSize: `${(block.fontSize || 10) * 0.8}px`,
          color: block.textColor || '#000000',
          textAlign: block.textAlign || 'left',
        }}
      >
        {block.content || blockType?.defaultContent}
      </div>
    );
  };

  return (
    <div
      ref={blockRef}
      className={`absolute cursor-move ${isSelected ? 'ring-2 ring-blue-500 z-20' : 'hover:ring-1 hover:ring-blue-300 z-10'}`}
      style={{
        left: `${block.x * scale}px`,
        top: `${block.y * scale}px`,
        width: `${block.width * scale}px`,
        height: `${block.height * scale}px`,
        backgroundColor: block.backgroundColor || (block.type === 'separator' ? 'transparent' : '#ffffff'),
        border: block.type === 'separator' ? 'none' : `${block.borderWidth || 0}px solid ${block.borderColor || '#cccccc'}`,
      }}
      onMouseDown={handleMouseDown}
      data-testid={`block-${block.id}`}
    >
      {/* Block content */}
      {getBlockContent()}
      
      {/* Selection overlay with info */}
      {isSelected && (
        <>
          {/* Position indicator */}
          <div className="absolute -top-5 left-0 bg-blue-500 text-white text-[10px] px-1 rounded">
            {block.x}mm, {block.y}mm
          </div>
          {/* Size indicator */}
          <div className="absolute -bottom-5 right-0 bg-blue-500 text-white text-[10px] px-1 rounded">
            {block.width}x{block.height}mm
          </div>
          {/* Resize handle */}
          <div
            className="resize-handle absolute bottom-0 right-0 w-3 h-3 bg-blue-500 cursor-se-resize"
            onMouseDown={handleResizeMouseDown}
          />
        </>
      )}
      
      {/* Block type indicator (small) */}
      {!isSelected && (
        <div className="absolute -top-4 left-0 flex items-center gap-1 bg-gray-700 text-white text-[8px] px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
          <Icon className="w-2 h-2" />
          {blockType?.name[language] || block.type}
        </div>
      )}
    </div>
  );
};

// Block Palette Component
const BlockPalette = ({ onAddBlock, language }) => {
  const t = language === 'fr' ? {
    addBlock: 'Ajouter un bloc',
    dragHint: 'Cliquez pour ajouter',
  } : {
    addBlock: 'Blok toevoegen',
    dragHint: 'Klik om toe te voegen',
  };

  return (
    <div className="p-3">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <Plus className="w-4 h-4" />
        {t.addBlock}
      </h3>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(BLOCK_TYPES).map(([type, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={type}
              onClick={() => onAddBlock(type)}
              className="flex items-center gap-2 p-2 text-xs bg-gray-50 hover:bg-amber-50 border border-gray-200 hover:border-amber-300 rounded transition-colors text-left"
              data-testid={`add-block-${type}`}
            >
              <Icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="truncate">{config.name[language]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Properties Panel Component
const PropertiesPanel = ({ 
  block, 
  onUpdate, 
  onDelete, 
  onDuplicate,
  onImageUpload,
  language 
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const t = language === 'fr' ? {
    properties: 'Propriétés',
    position: 'Position',
    size: 'Taille',
    content: 'Contenu',
    style: 'Style',
    font: 'Police',
    fontSize: 'Taille police',
    textColor: 'Couleur texte',
    bgColor: 'Couleur fond',
    borderColor: 'Couleur bordure',
    borderWidth: 'Bordure (px)',
    alignment: 'Alignement',
    delete: 'Supprimer',
    duplicate: 'Dupliquer',
    uploadImage: 'Télécharger image',
    headerColor: 'Couleur en-tête',
    altRowColor: 'Couleur ligne alternée',
    noSelection: 'Sélectionnez un bloc pour modifier ses propriétés',
  } : {
    properties: 'Eigenschappen',
    position: 'Positie',
    size: 'Grootte',
    content: 'Inhoud',
    style: 'Stijl',
    font: 'Lettertype',
    fontSize: 'Lettergrootte',
    textColor: 'Tekstkleur',
    bgColor: 'Achtergrondkleur',
    borderColor: 'Randkleur',
    borderWidth: 'Rand (px)',
    alignment: 'Uitlijning',
    delete: 'Verwijderen',
    duplicate: 'Dupliceren',
    uploadImage: 'Afbeelding uploaden',
    headerColor: 'Koptekstkleur',
    altRowColor: 'Alternatieve rijkleur',
    noSelection: 'Selecteer een blok om eigenschappen te bewerken',
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        onUpdate('imageUrl', `${BACKEND_URL}${data.url}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  if (!block) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        <Square className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        {t.noSelection}
      </div>
    );
  }

  const blockType = BLOCK_TYPES[block.type];

  return (
    <div className="p-3 space-y-4 text-sm">
      {/* Block type header */}
      <div className="flex items-center justify-between pb-2 border-b">
        <div className="flex items-center gap-2">
          {blockType && <blockType.icon className="w-4 h-4 text-amber-600" />}
          <span className="font-semibold">{blockType?.name[language] || block.type}</span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => onDuplicate(block.id)}
            className="p-1.5 hover:bg-gray-100 rounded"
            title={t.duplicate}
          >
            <Copy className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => onDelete(block.id)}
            className="p-1.5 hover:bg-red-100 rounded"
            title={t.delete}
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>

      {/* Position */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t.position} (mm)</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-xs text-gray-400">X</span>
            <input
              type="number"
              value={block.x}
              onChange={(e) => onUpdate('x', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 border rounded text-sm"
              min="0"
              max={A4_WIDTH - block.width}
              step={GRID_SIZE}
            />
          </div>
          <div>
            <span className="text-xs text-gray-400">Y</span>
            <input
              type="number"
              value={block.y}
              onChange={(e) => onUpdate('y', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 border rounded text-sm"
              min="0"
              max={A4_HEIGHT - block.height}
              step={GRID_SIZE}
            />
          </div>
        </div>
      </div>

      {/* Size */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t.size} (mm)</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-xs text-gray-400">W</span>
            <input
              type="number"
              value={block.width}
              onChange={(e) => onUpdate('width', parseFloat(e.target.value) || 10)}
              className="w-full px-2 py-1 border rounded text-sm"
              min="10"
              max={A4_WIDTH - block.x}
              step={GRID_SIZE}
            />
          </div>
          <div>
            <span className="text-xs text-gray-400">H</span>
            <input
              type="number"
              value={block.height}
              onChange={(e) => onUpdate('height', parseFloat(e.target.value) || 5)}
              className="w-full px-2 py-1 border rounded text-sm"
              min="1"
              max={A4_HEIGHT - block.y}
              step={GRID_SIZE}
            />
          </div>
        </div>
      </div>

      {/* Image upload for logo/image blocks */}
      {(block.type === 'logo' || block.type === 'free_image') && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t.uploadImage}</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full px-3 py-2 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {uploading ? '...' : t.uploadImage}
          </button>
          {block.imageUrl && (
            <img src={block.imageUrl} alt="" className="mt-2 max-h-20 mx-auto" />
          )}
        </div>
      )}

      {/* Content (for text blocks) */}
      {!['logo', 'free_image', 'separator', 'qr_code', 'items_table'].includes(block.type) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t.content}</label>
          <textarea
            value={block.content || ''}
            onChange={(e) => onUpdate('content', e.target.value)}
            rows={3}
            className="w-full px-2 py-1 border rounded text-sm"
            placeholder={blockType?.defaultContent}
          />
        </div>
      )}

      {/* Font settings (for text blocks) */}
      {!['logo', 'free_image', 'separator', 'qr_code'].includes(block.type) && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t.font}</label>
              <select
                value={block.fontFamily || 'Helvetica'}
                onChange={(e) => onUpdate('fontFamily', e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
              >
                {FONTS.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t.fontSize}</label>
              <select
                value={block.fontSize || 10}
                onChange={(e) => onUpdate('fontSize', parseInt(e.target.value))}
                className="w-full px-2 py-1 border rounded text-sm"
              >
                {FONT_SIZES.map(s => <option key={s} value={s}>{s}pt</option>)}
              </select>
            </div>
          </div>

          {/* Text alignment */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t.alignment}</label>
            <div className="flex gap-1">
              {[
                { value: 'left', icon: AlignLeft },
                { value: 'center', icon: AlignCenter },
                { value: 'right', icon: AlignRight },
              ].map(({ value, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => onUpdate('textAlign', value)}
                  className={`flex-1 p-2 rounded border ${
                    block.textAlign === value ? 'bg-amber-100 border-amber-500' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4 mx-auto" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Colors */}
      <div className="grid grid-cols-2 gap-2">
        {!['separator'].includes(block.type) && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t.textColor}</label>
              <input
                type="color"
                value={block.textColor || '#000000'}
                onChange={(e) => onUpdate('textColor', e.target.value)}
                className="w-full h-8 rounded border cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t.bgColor}</label>
              <input
                type="color"
                value={block.backgroundColor || '#ffffff'}
                onChange={(e) => onUpdate('backgroundColor', e.target.value)}
                className="w-full h-8 rounded border cursor-pointer"
              />
            </div>
          </>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t.borderColor}</label>
          <input
            type="color"
            value={block.borderColor || '#cccccc'}
            onChange={(e) => onUpdate('borderColor', e.target.value)}
            className="w-full h-8 rounded border cursor-pointer"
          />
        </div>
        {block.type !== 'separator' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t.borderWidth}</label>
            <input
              type="number"
              value={block.borderWidth || 0}
              onChange={(e) => onUpdate('borderWidth', parseInt(e.target.value) || 0)}
              className="w-full px-2 py-1 border rounded text-sm"
              min="0"
              max="5"
            />
          </div>
        )}
      </div>

      {/* Table-specific settings */}
      {block.type === 'items_table' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t.headerColor}</label>
            <input
              type="color"
              value={block.headerColor || '#8B4513'}
              onChange={(e) => onUpdate('headerColor', e.target.value)}
              className="w-full h-8 rounded border cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t.altRowColor}</label>
            <input
              type="color"
              value={block.altRowColor || '#f9f9f9'}
              onChange={(e) => onUpdate('altRowColor', e.target.value)}
              className="w-full h-8 rounded border cursor-pointer"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Main Advanced Template Editor Component
const AdvancedTemplateEditor = ({ 
  template, 
  onSave, 
  onClose, 
  language = 'fr',
  documentType = 'invoice' // 'invoice' or 'credit_note'
}) => {
  const canvasRef = useRef(null);
  const watermarkInputRef = useRef(null);
  const [blocks, setBlocks] = useState(template?.blocks || []);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [templateName, setTemplateName] = useState(template?.name || '');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('sample'); // 'sample' or 'empty'
  const [showPageSettings, setShowPageSettings] = useState(false);
  
  // Page settings
  const [pageBackgroundColor, setPageBackgroundColor] = useState(template?.pageBackgroundColor || '#FFFFFF');
  const [watermarkUrl, setWatermarkUrl] = useState(template?.watermarkUrl || '');
  const [watermarkOpacity, setWatermarkOpacity] = useState(template?.watermarkOpacity || 15);
  const [watermarkSize, setWatermarkSize] = useState(template?.watermarkSize || 50); // percentage of page width
  const [uploadingWatermark, setUploadingWatermark] = useState(false);

  const scale = SCALE_FACTOR * (zoom / 100);
  const previewScale = 1.8; // Smaller scale for preview

  // Sample data for preview
  const sampleData = language === 'fr' ? {
    company: {
      name: 'EBB-SHOP SPRL',
      address: '123 Rue du Commerce\n1000 Bruxelles\nBelgique',
      vat: 'BE0123456789',
      email: 'contact@ebb-shop.be',
      phone: '+32 2 123 45 67',
      iban: 'BE68 5390 0754 7034',
    },
    invoice: {
      number: 'FAC-2024-00042',
      date: '15/01/2024',
      dueDate: '14/02/2024',
    },
    client: {
      name: 'Jean Dupont',
      company: 'Écurie du Soleil',
      address: '456 Avenue des Chevaux\n2000 Anvers\nBelgique',
      vat: 'BE9876543210',
    },
    items: [
      { description: 'Selle équestre Premium', qty: 1, priceHT: 413.22, total: 413.22 },
      { description: 'Bride en cuir', qty: 2, priceHT: 82.64, total: 165.28 },
      { description: 'Tapis de selle', qty: 1, priceHT: 49.59, total: 49.59 },
    ],
    subtotalHT: 628.09,
    vat: 131.90,
    totalTTC: 759.99,
  } : {
    company: {
      name: 'EBB-SHOP BVBA',
      address: '123 Handelsstraat\n1000 Brussel\nBelgië',
      vat: 'BE0123456789',
      email: 'contact@ebb-shop.be',
      phone: '+32 2 123 45 67',
      iban: 'BE68 5390 0754 7034',
    },
    invoice: {
      number: 'FAC-2024-00042',
      date: '15/01/2024',
      dueDate: '14/02/2024',
    },
    client: {
      name: 'Jan Janssen',
      company: 'Stal de Zon',
      address: '456 Paardenlaan\n2000 Antwerpen\nBelgië',
      vat: 'BE9876543210',
    },
    items: [
      { description: 'Premium rijzadel', qty: 1, priceHT: 413.22, total: 413.22 },
      { description: 'Leren hoofdstel', qty: 2, priceHT: 82.64, total: 165.28 },
      { description: 'Zadeldek', qty: 1, priceHT: 49.59, total: 49.59 },
    ],
    subtotalHT: 628.09,
    vat: 131.90,
    totalTTC: 759.99,
  };

  const t = language === 'fr' ? {
    title: documentType === 'credit_note' ? 'Éditeur de Note de Crédit' : 'Éditeur de Facture',
    save: 'Enregistrer',
    cancel: 'Annuler',
    templateName: 'Nom du modèle',
    showGrid: 'Afficher grille',
    snapToGrid: 'Aligner sur grille',
    zoom: 'Zoom',
    addBlocks: 'Blocs disponibles',
    properties: 'Propriétés',
    canvas: 'Zone de travail A4',
    undo: 'Annuler',
    redo: 'Rétablir',
    preview: 'Aperçu',
    livePreview: 'Aperçu en direct',
    showPreview: 'Afficher aperçu',
    hidePreview: 'Masquer aperçu',
    sampleData: 'Données exemple',
    emptyTemplate: 'Template vide',
    invoice: 'FACTURE',
    creditNote: 'NOTE DE CRÉDIT',
    pageSettings: 'Paramètres page',
    backgroundColor: 'Couleur de fond',
    watermark: 'Filigrane',
    uploadWatermark: 'Télécharger filigrane',
    removeWatermark: 'Supprimer filigrane',
    watermarkOpacity: 'Opacité (%)',
    watermarkSize: 'Taille (%)',
    watermarkHint: 'Image en arrière-plan (logo, texte...)',
  } : {
    title: documentType === 'credit_note' ? 'Creditnota Editor' : 'Factuur Editor',
    save: 'Opslaan',
    cancel: 'Annuleren',
    templateName: 'Sjabloonnaam',
    showGrid: 'Raster tonen',
    snapToGrid: 'Uitlijnen op raster',
    zoom: 'Zoom',
    addBlocks: 'Beschikbare blokken',
    properties: 'Eigenschappen',
    canvas: 'A4 werkgebied',
    undo: 'Ongedaan maken',
    redo: 'Opnieuw',
    preview: 'Voorbeeld',
    livePreview: 'Live voorbeeld',
    showPreview: 'Toon voorbeeld',
    hidePreview: 'Verberg voorbeeld',
    sampleData: 'Voorbeeldgegevens',
    emptyTemplate: 'Lege sjabloon',
    invoice: 'FACTUUR',
    creditNote: 'CREDITNOTA',
    pageSettings: 'Pagina-instellingen',
    backgroundColor: 'Achtergrondkleur',
    watermark: 'Watermerk',
    uploadWatermark: 'Watermerk uploaden',
    removeWatermark: 'Watermerk verwijderen',
    watermarkOpacity: 'Dekking (%)',
    watermarkSize: 'Grootte (%)',
    watermarkHint: 'Achtergrondafbeelding (logo, tekst...)',
  };

  // Save to history for undo/redo
  const saveToHistory = useCallback((newBlocks) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newBlocks));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setBlocks(JSON.parse(history[historyIndex - 1]));
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setBlocks(JSON.parse(history[historyIndex + 1]));
    }
  };

  const handleAddBlock = (type) => {
    const blockConfig = BLOCK_TYPES[type];
    const newBlock = {
      id: `block_${Date.now()}`,
      type,
      x: 10,
      y: 10 + blocks.length * 10,
      width: blockConfig.defaultWidth,
      height: blockConfig.defaultHeight,
      content: blockConfig.defaultContent,
      fontFamily: 'Helvetica',
      fontSize: 10,
      textColor: '#000000',
      backgroundColor: type === 'separator' ? 'transparent' : '#ffffff',
      borderColor: '#cccccc',
      borderWidth: type === 'separator' ? 0 : 0,
      textAlign: 'left',
    };
    const newBlocks = [...blocks, newBlock];
    setBlocks(newBlocks);
    setSelectedBlockId(newBlock.id);
    saveToHistory(newBlocks);
  };

  const handleMoveBlock = (blockId, x, y) => {
    setBlocks(prev => prev.map(b => 
      b.id === blockId ? { ...b, x, y } : b
    ));
  };

  const handleResizeBlock = (blockId, width, height) => {
    setBlocks(prev => prev.map(b => 
      b.id === blockId ? { ...b, width, height } : b
    ));
  };

  const handleUpdateBlock = (property, value) => {
    if (!selectedBlockId) return;
    const newBlocks = blocks.map(b => 
      b.id === selectedBlockId ? { ...b, [property]: value } : b
    );
    setBlocks(newBlocks);
  };

  const handleDeleteBlock = (blockId) => {
    const newBlocks = blocks.filter(b => b.id !== blockId);
    setBlocks(newBlocks);
    setSelectedBlockId(null);
    saveToHistory(newBlocks);
  };

  const handleDuplicateBlock = (blockId) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    const newBlock = {
      ...block,
      id: `block_${Date.now()}`,
      x: block.x + 5,
      y: block.y + 5,
    };
    const newBlocks = [...blocks, newBlock];
    setBlocks(newBlocks);
    setSelectedBlockId(newBlock.id);
    saveToHistory(newBlocks);
  };

  const handleSave = () => {
    onSave({
      ...template,
      name: templateName,
      blocks,
      documentType,
      pageBackgroundColor,
      watermarkUrl,
      watermarkOpacity,
      watermarkSize,
    });
  };

  // Handle watermark upload
  const handleWatermarkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingWatermark(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setWatermarkUrl(`${BACKEND_URL}${data.url}`);
      }
    } catch (error) {
      console.error('Watermark upload error:', error);
    } finally {
      setUploadingWatermark(false);
    }
  };

  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current || e.target.classList.contains('grid-overlay')) {
      setSelectedBlockId(null);
    }
  };

  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  // Generate grid lines
  const gridLines = [];
  if (showGrid) {
    // Vertical lines
    for (let x = 0; x <= A4_WIDTH; x += GRID_SIZE) {
      gridLines.push(
        <line
          key={`v-${x}`}
          x1={x * scale}
          y1={0}
          x2={x * scale}
          y2={A4_HEIGHT * scale}
          stroke={x % 10 === 0 ? '#ccc' : '#eee'}
          strokeWidth={x % 10 === 0 ? 0.5 : 0.25}
        />
      );
    }
    // Horizontal lines
    for (let y = 0; y <= A4_HEIGHT; y += GRID_SIZE) {
      gridLines.push(
        <line
          key={`h-${y}`}
          x1={0}
          y1={y * scale}
          x2={A4_WIDTH * scale}
          y2={y * scale}
          stroke={y % 10 === 0 ? '#ccc' : '#eee'}
          strokeWidth={y % 10 === 0 ? 0.5 : 0.25}
        />
      );
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 p-3 border-b flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold flex items-center gap-2 text-amber-900">
              <FileText className="w-5 h-5" />
              {t.title}
            </h2>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder={t.templateName}
              className="px-3 py-1.5 border rounded-lg text-sm w-48"
            />
          </div>
          
          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              className="p-2 hover:bg-amber-100 rounded disabled:opacity-50"
              title={t.undo}
            >
              <Undo className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              className="p-2 hover:bg-amber-100 rounded disabled:opacity-50"
              title={t.redo}
            >
              <Redo className="w-4 h-4" />
            </button>
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
                className="rounded"
              />
              <Grid3X3 className="w-4 h-4" />
              {t.showGrid}
            </label>
            
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={snapToGrid}
                onChange={(e) => setSnapToGrid(e.target.checked)}
                className="rounded"
              />
              <Move className="w-4 h-4" />
              {t.snapToGrid}
            </label>
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            <div className="flex items-center gap-1">
              <button
                onClick={() => setZoom(Math.max(50, zoom - 10))}
                className="p-1 hover:bg-amber-100 rounded"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm w-12 text-center">{zoom}%</span>
              <button
                onClick={() => setZoom(Math.min(150, zoom + 10))}
                className="p-1 hover:bg-amber-100 rounded"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            {/* Page settings button */}
            <button
              onClick={() => setShowPageSettings(!showPageSettings)}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${
                showPageSettings 
                  ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="page-settings-btn"
            >
              <Palette className="w-4 h-4" />
              {t.pageSettings}
            </button>
            
            {/* Preview toggle */}
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${
                showPreview 
                  ? 'bg-green-100 text-green-700 border border-green-300' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="toggle-preview-btn"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? t.hidePreview : t.showPreview}
            </button>
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              {t.cancel}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
              data-testid="save-template-btn"
            >
              <Save className="w-4 h-4" />
              {t.save}
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Block Palette */}
          <div className="w-56 bg-gray-50 border-r overflow-y-auto flex-shrink-0">
            <BlockPalette onAddBlock={handleAddBlock} language={language} />
            
            {/* Page Settings Panel (collapsible) */}
            {showPageSettings && (
              <div className="border-t p-3 space-y-4">
                <h3 className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  {t.pageSettings}
                </h3>
                
                {/* Background Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t.backgroundColor}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={pageBackgroundColor}
                      onChange={(e) => setPageBackgroundColor(e.target.value)}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={pageBackgroundColor}
                      onChange={(e) => setPageBackgroundColor(e.target.value)}
                      className="flex-1 px-2 py-1 border rounded text-xs"
                    />
                  </div>
                </div>
                
                {/* Watermark */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t.watermark}
                  </label>
                  <input
                    ref={watermarkInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleWatermarkUpload}
                    className="hidden"
                  />
                  
                  {watermarkUrl ? (
                    <div className="space-y-2">
                      <div className="relative w-full h-20 bg-gray-100 rounded border flex items-center justify-center">
                        <img 
                          src={watermarkUrl} 
                          alt="Filigrane" 
                          className="max-h-full max-w-full object-contain"
                          style={{ opacity: watermarkOpacity / 100 }}
                        />
                      </div>
                      <button
                        onClick={() => setWatermarkUrl('')}
                        className="w-full px-2 py-1.5 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        {t.removeWatermark}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => watermarkInputRef.current?.click()}
                      disabled={uploadingWatermark}
                      className="w-full px-3 py-2 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200 flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingWatermark ? '...' : t.uploadWatermark}
                    </button>
                  )}
                  <p className="text-[10px] text-gray-500 mt-1">{t.watermarkHint}</p>
                </div>
                
                {/* Watermark Settings (only if watermark exists) */}
                {watermarkUrl && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t.watermarkOpacity}: {watermarkOpacity}%
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        value={watermarkOpacity}
                        onChange={(e) => setWatermarkOpacity(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t.watermarkSize}: {watermarkSize}%
                      </label>
                      <input
                        type="range"
                        min="20"
                        max="100"
                        value={watermarkSize}
                        onChange={(e) => setWatermarkSize(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Center - Canvas */}
          <div className="flex-1 bg-gray-200 overflow-auto p-8 flex items-start justify-center">
            <div 
              ref={canvasRef}
              className="relative shadow-xl"
              style={{
                width: `${A4_WIDTH * scale}px`,
                height: `${A4_HEIGHT * scale}px`,
                backgroundColor: pageBackgroundColor,
              }}
              onClick={handleCanvasClick}
            >
              {/* Watermark */}
              {watermarkUrl && (
                <div 
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  style={{ opacity: watermarkOpacity / 100 }}
                >
                  <img 
                    src={watermarkUrl} 
                    alt="" 
                    style={{ 
                      width: `${watermarkSize}%`,
                      maxHeight: '80%',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              )}
              
              {/* Grid overlay */}
              {showGrid && (
                <svg 
                  className="absolute inset-0 pointer-events-none grid-overlay"
                  width={A4_WIDTH * scale}
                  height={A4_HEIGHT * scale}
                >
                  {gridLines}
                </svg>
              )}

              {/* Blocks */}
              {blocks.map(block => (
                <DraggableBlock
                  key={block.id}
                  block={block}
                  isSelected={selectedBlockId === block.id}
                  onSelect={setSelectedBlockId}
                  onMove={handleMoveBlock}
                  onResize={handleResizeBlock}
                  scale={scale}
                  snapToGrid={snapToGrid}
                  language={language}
                />
              ))}

              {/* Page margins indicator */}
              <div 
                className="absolute border border-dashed border-blue-200 pointer-events-none"
                style={{
                  left: `${10 * scale}px`,
                  top: `${10 * scale}px`,
                  right: `${10 * scale}px`,
                  bottom: `${10 * scale}px`,
                }}
              />
            </div>
          </div>

          {/* Live Preview Panel */}
          {showPreview && (
            <div className="w-[380px] bg-gray-100 border-l overflow-y-auto flex-shrink-0">
              <div className="p-3 bg-green-50 border-b flex items-center justify-between">
                <h3 className="text-sm font-semibold text-green-700 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  {t.livePreview}
                </h3>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPreviewMode('sample')}
                    className={`px-2 py-1 text-xs rounded ${previewMode === 'sample' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}
                  >
                    {t.sampleData}
                  </button>
                  <button
                    onClick={() => setPreviewMode('empty')}
                    className={`px-2 py-1 text-xs rounded ${previewMode === 'empty' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}
                  >
                    {t.emptyTemplate}
                  </button>
                </div>
              </div>
              
              <div className="p-4 flex justify-center">
                {/* Preview A4 Page */}
                <div 
                  className="shadow-lg relative overflow-hidden"
                  style={{
                    width: `${A4_WIDTH * previewScale}px`,
                    height: `${A4_HEIGHT * previewScale}px`,
                    fontSize: `${previewScale * 5}px`,
                    backgroundColor: pageBackgroundColor,
                  }}
                >
                  {/* Watermark in preview */}
                  {watermarkUrl && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      style={{ opacity: watermarkOpacity / 100 }}
                    >
                      <img 
                        src={watermarkUrl} 
                        alt="" 
                        style={{ 
                          width: `${watermarkSize}%`,
                          maxHeight: '80%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                  )}
                  
                  {blocks.map(block => {
                    const blockType = BLOCK_TYPES[block.type];
                    
                    // Get content based on preview mode
                    const getPreviewContent = () => {
                      if (previewMode === 'empty') {
                        return block.content || blockType?.defaultContent || '';
                      }
                      
                      // Use sample data for preview
                      switch (block.type) {
                        case 'company_header':
                          return `${sampleData.company.name}\n${sampleData.company.address}\nTVA: ${sampleData.company.vat}`;
                        case 'invoice_info':
                          return `${documentType === 'credit_note' ? t.creditNote : t.invoice} N°: ${sampleData.invoice.number}\nDate: ${sampleData.invoice.date}\n${language === 'fr' ? 'Échéance' : 'Vervaldatum'}: ${sampleData.invoice.dueDate}`;
                        case 'client_info':
                          return `${language === 'fr' ? 'Facturé à:' : 'Gefactureerd aan:'}\n${sampleData.client.name}\n${sampleData.client.company}\n${sampleData.client.address}`;
                        case 'totals':
                          return `${language === 'fr' ? 'Sous-total HTVA' : 'Subtotaal excl. BTW'}: €${sampleData.subtotalHT.toFixed(2)}\nTVA 21%: €${sampleData.vat.toFixed(2)}\nTOTAL TTC: €${sampleData.totalTTC.toFixed(2)}`;
                        case 'payment_conditions':
                          return `${language === 'fr' ? 'Paiement sous 30 jours' : 'Betaling binnen 30 dagen'}. IBAN: ${sampleData.company.iban}`;
                        case 'footer':
                          return language === 'fr' ? 'Merci pour votre confiance.' : 'Bedankt voor uw vertrouwen.';
                        default:
                          return block.content || blockType?.defaultContent || '';
                      }
                    };
                    
                    if (block.type === 'separator') {
                      return (
                        <div
                          key={block.id}
                          className="absolute"
                          style={{
                            left: `${block.x * previewScale}px`,
                            top: `${block.y * previewScale}px`,
                            width: `${block.width * previewScale}px`,
                            height: `${Math.max(1, block.height) * previewScale}px`,
                            backgroundColor: block.borderColor || '#000000',
                          }}
                        />
                      );
                    }
                    
                    if (block.type === 'logo' || block.type === 'free_image') {
                      return (
                        <div
                          key={block.id}
                          className="absolute flex items-center justify-center"
                          style={{
                            left: `${block.x * previewScale}px`,
                            top: `${block.y * previewScale}px`,
                            width: `${block.width * previewScale}px`,
                            height: `${block.height * previewScale}px`,
                            backgroundColor: block.backgroundColor || 'transparent',
                          }}
                        >
                          {block.imageUrl ? (
                            <img src={block.imageUrl} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <div className="w-full h-full bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-[10px]">
                              LOGO
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    if (block.type === 'qr_code') {
                      return (
                        <div
                          key={block.id}
                          className="absolute flex flex-col items-center justify-center"
                          style={{
                            left: `${block.x * previewScale}px`,
                            top: `${block.y * previewScale}px`,
                            width: `${block.width * previewScale}px`,
                            height: `${block.height * previewScale}px`,
                            backgroundColor: block.backgroundColor || '#ffffff',
                          }}
                        >
                          <div className="w-3/4 h-3/4 border border-gray-400 flex items-center justify-center text-[8px] text-gray-500">
                            QR
                          </div>
                          <span className="text-[6px] text-gray-500 mt-0.5">
                            {language === 'fr' ? 'Scannez pour payer' : 'Scan om te betalen'}
                          </span>
                        </div>
                      );
                    }
                    
                    if (block.type === 'items_table') {
                      return (
                        <div
                          key={block.id}
                          className="absolute overflow-hidden"
                          style={{
                            left: `${block.x * previewScale}px`,
                            top: `${block.y * previewScale}px`,
                            width: `${block.width * previewScale}px`,
                            height: `${block.height * previewScale}px`,
                            fontSize: `${(block.fontSize || 10) * 0.6}px`,
                          }}
                        >
                          <table className="w-full border-collapse text-[8px]">
                            <thead>
                              <tr style={{ backgroundColor: block.headerColor || '#8B4513', color: '#fff' }}>
                                <th className="p-1 text-left border">Description</th>
                                <th className="p-1 text-right border w-8">{language === 'fr' ? 'Qté' : 'Aantal'}</th>
                                <th className="p-1 text-right border w-12">{language === 'fr' ? 'Prix HT' : 'Prijs'}</th>
                                <th className="p-1 text-right border w-12">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(previewMode === 'sample' ? sampleData.items : [
                                { description: 'Article exemple', qty: 1, priceHT: 0, total: 0 }
                              ]).map((item, idx) => (
                                <tr key={idx} style={{ backgroundColor: idx % 2 === 1 ? (block.altRowColor || '#f9f9f9') : '#fff' }}>
                                  <td className="p-1 border">{item.description}</td>
                                  <td className="p-1 text-right border">{item.qty}</td>
                                  <td className="p-1 text-right border">€{item.priceHT.toFixed(2)}</td>
                                  <td className="p-1 text-right border">€{item.total.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    
                    // Text blocks
                    return (
                      <div
                        key={block.id}
                        className="absolute whitespace-pre-wrap overflow-hidden"
                        style={{
                          left: `${block.x * previewScale}px`,
                          top: `${block.y * previewScale}px`,
                          width: `${block.width * previewScale}px`,
                          height: `${block.height * previewScale}px`,
                          fontFamily: block.fontFamily || 'Helvetica',
                          fontSize: `${(block.fontSize || 10) * 0.6}px`,
                          color: block.textColor || '#000000',
                          backgroundColor: block.backgroundColor || '#ffffff',
                          border: (block.borderWidth || 0) > 0 ? `${block.borderWidth}px solid ${block.borderColor || '#ccc'}` : 'none',
                          textAlign: block.textAlign || 'left',
                          padding: `${(block.padding || 2) * 0.5}px`,
                          lineHeight: 1.3,
                        }}
                      >
                        {getPreviewContent()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Right Panel - Properties */}
          <div className="w-64 bg-white border-l overflow-y-auto flex-shrink-0">
            <div className="p-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                {t.properties}
              </h3>
            </div>
            <PropertiesPanel
              block={selectedBlock}
              onUpdate={handleUpdateBlock}
              onDelete={handleDeleteBlock}
              onDuplicate={handleDuplicateBlock}
              language={language}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedTemplateEditor;
